import { useCallback, useState } from 'react'

import type { ProcessImageParameters } from '@ai-color-modules/gen_proto/ai_color_simulation/v1/messages_pb'
import type { ColorOption } from '@ai-color-modules/simulation/ColorPalette/colorOptions'
import type { MaterialOption } from '@ai-color-modules/simulation/MaterialPalette/materialOptions'

import { buildSummaryRows } from '../utils/buildSummaryRows'
import { composeSummaryImage } from '../utils/composeSummaryImage'

interface UseSummaryImageDownloadOptions {
  /** 現況写真（Before）URL */
  beforeImageUrl?: string
  /** 表示中の結果画像（After）URL */
  afterImageUrl?: string
  /** タイトルに使う案件名 */
  projectName: string
  /** 表示中の結果画像が生成された時のパラメータ。サマリ行はここから組み立てる */
  parameters?: ProcessImageParameters
  availableColors: ColorOption[]
  availableMaterials: MaterialOption[]
  /** 右上に描画する会社名（company role のみ） */
  companyName?: string
  /** 右上に描画する会社ロゴ URL（未設定／読込失敗時は会社名のみ） */
  companyLogoUrl?: string
  /** ファイル名のプレフィックス */
  filenamePrefix?: string
  onError?: (error: Error) => void
}

interface UseSummaryImageDownloadReturn {
  download: () => Promise<void>
  isDownloading: boolean
}

/**
 * 結果画像 + 部位ごとの色／素材サマリを 1 枚の PNG にしてダウンロードする hook.
 *
 * 重要: サマリの中身は **表示中の結果画像が生成された時のパラメータ** から組み立てる。
 * 編集中フォーム state は使わない（仕様）。
 */
export const useSummaryImageDownload = ({
  beforeImageUrl,
  afterImageUrl,
  projectName,
  parameters,
  availableColors,
  availableMaterials,
  companyName,
  companyLogoUrl,
  filenamePrefix = '生成画像',
  onError,
}: UseSummaryImageDownloadOptions): UseSummaryImageDownloadReturn => {
  const [isDownloading, setIsDownloading] = useState(false)

  const download = useCallback(async () => {
    if (!afterImageUrl || !beforeImageUrl || !parameters) {
      return
    }

    setIsDownloading(true)
    try {
      const rows = buildSummaryRows({ parameters, availableColors, availableMaterials })
      const blob = await composeSummaryImage({
        projectName,
        beforeImageUrl,
        afterImageUrl,
        rows,
        companyName,
        companyLogoUrl,
      })

      const timestamp = new Date().toISOString().slice(0, 19).replace('T', '_').replace(/:/g, '-')
      const filename = `${filenamePrefix}_summary_${timestamp}.png`

      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    } catch (e) {
      const err = e instanceof Error ? e : new Error('サマリ画像の生成に失敗しました')
      console.error('[useSummaryImageDownload] サマリ画像の生成に失敗しました', e)
      onError?.(err)
    } finally {
      setIsDownloading(false)
    }
  }, [
    beforeImageUrl,
    afterImageUrl,
    projectName,
    parameters,
    availableColors,
    availableMaterials,
    companyName,
    companyLogoUrl,
    filenamePrefix,
    onError,
  ])

  return { download, isDownloading }
}
